import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, STATUS_LABEL, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus, ArrowDownToLine, Trash2 } from "lucide-react";
import AddWorkDialog from "@/components/AddWorkDialog";
import ReceiveWorkDialog from "@/components/ReceiveWorkDialog";
import { toast } from "sonner";

export default function WorkerDetail() {
  const { id } = useParams();
  const [worker, setWorker] = useState(null);
  const [works, setWorks] = useState([]);
  const [stats, setStats] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [receiveWork, setReceiveWork] = useState(null);

  const load = async () => {
    try {
      const [ws, wks, s] = await Promise.all([
        api.get("/workers"),
        api.get("/works", { params: { worker_id: id } }),
        api.get("/stats/by-worker"),
      ]);
      setWorker(ws.data.find(w => w.id === id));
      setWorks(wks.data);
      setStats(s.data.find(x => x.worker_id === id));
    } catch {
      toast.error("Yüklenemedi");
    }
  };
  useEffect(() => { load(); }, [id]);

  const deleteWork = async (wid) => {
    if (!window.confirm("Bu iş kaydını silmek istiyor musunuz?")) return;
    try {
      await api.delete(`/works/${wid}`);
      toast.success("İş silindi");
      load();
    } catch {
      toast.error("Silinemedi");
    }
  };

  if (!worker) return <div className="text-[#5C605C]">Yükleniyor...</div>;

  return (
    <div className="space-y-8">
      <Link to="/workers" className="inline-flex items-center gap-1.5 text-sm text-[#3B5249] hover:text-[#2C3E37] transition-colors" data-testid="back-to-workers">
        <ArrowLeft size={16} /> Çalışanlar
      </Link>

      <section className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 fade-up">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#3B5249] to-[#5C7D64] text-white grid place-items-center font-display font-semibold text-2xl">
            {worker.name.charAt(0)}
          </div>
          <div>
            <div className="text-xs font-semibold tracking-[0.22em] uppercase text-[#D98359]">Çalışan Detayı</div>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold text-[#1A1D1A]">{worker.name}</h1>
          </div>
        </div>
        <Button onClick={() => setOpenAdd(true)} className="rounded-full h-12 px-6 bg-[#3B5249] hover:bg-[#2C3E37] text-white shadow-soft transition-colors" data-testid="add-work-for-worker">
          <Plus size={18} className="mr-1.5" />
          {worker.name}&apos;e İş Ver
        </Button>
      </section>

      {stats && (
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Toplam İş", value: stats.total_works, color: "text-[#3B5249]", bg: "bg-[#EFF1EE]" },
            { label: "Devam Eden", value: stats.in_progress, color: "text-[#A85F26]", bg: "bg-[#FBEEDF]" },
            { label: "Tamamlanan", value: stats.completed, color: "text-[#3F5B47]", bg: "bg-[#EBF1EC]" },
            { label: "Bekleyen Adet", value: stats.pending_quantity, color: "text-[#1A1D1A]", bg: "bg-[#F1F1EE]" },
          ].map((s) => (
            <Card key={s.label} className={`rounded-2xl border border-[#E6E6E2] bg-white shadow-soft p-5`}>
              <div className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-widest uppercase ${s.bg} ${s.color}`}>{s.label}</div>
              <div className={`mt-3 font-display text-3xl font-semibold ${s.color}`}>{s.value}</div>
            </Card>
          ))}
        </section>
      )}

      <section>
        <h2 className="font-display text-xl font-medium text-[#1A1D1A] mb-4">İş Geçmişi</h2>
        {works.length === 0 ? (
          <Card className="rounded-2xl border border-dashed border-[#D6D6CE] bg-white p-10 text-center">
            <div className="text-[#5C605C]">Bu çalışan için henüz iş kaydı yok.</div>
          </Card>
        ) : (
          <div className="space-y-3">
            {works.map((w, i) => (
              <Card key={w.id} className="rounded-2xl border border-[#E6E6E2] bg-white shadow-soft p-5 fade-up" style={{ animationDelay: `${i * 30}ms` }} data-testid={`worker-work-${w.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold text-[#1A1D1A]">{w.work_type}</span>
                      <span className={`status-pill status-${w.status}`}>{STATUS_LABEL[w.status]}</span>
                    </div>
                    <div className="text-sm text-[#5C605C] mt-1">
                      Veriliş: {formatDate(w.given_date)}
                      {w.received_date && ` · Alınış: ${formatDate(w.received_date)}`}
                    </div>
                    {w.description && <div className="text-sm text-[#3D403D] mt-2">{w.description}</div>}
                  </div>
                  <div className="flex items-center gap-6 shrink-0">
                    <div className="text-right">
                      <div className="text-[10px] font-semibold tracking-widest uppercase text-[#5C605C]">Verilen</div>
                      <div className="font-display font-semibold text-[#3B5249]">{w.quantity_given}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-semibold tracking-widest uppercase text-[#5C605C]">Alınan</div>
                      <div className="font-display font-semibold text-[#5C7D64]">{w.quantity_received}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-semibold tracking-widest uppercase text-[#5C605C]">Kalan</div>
                      <div className="font-display font-semibold text-[#A85F26]">{w.quantity_given - w.quantity_received}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  {w.status !== "completed" && (
                    <Button onClick={() => setReceiveWork(w)} className="rounded-full h-9 px-4 bg-[#5C7D64] hover:bg-[#4A6752] text-white text-sm transition-colors" data-testid={`receive-btn-detail-${w.id}`}>
                      <ArrowDownToLine size={13} className="mr-1.5" /> Teslim Al
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => deleteWork(w.id)} className="rounded-full h-9 px-4 text-[#A85F26] hover:bg-[#FBEEDF] text-sm transition-colors" data-testid={`delete-btn-detail-${w.id}`}>
                    <Trash2 size={13} className="mr-1.5" /> Sil
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <AddWorkDialog open={openAdd} onOpenChange={setOpenAdd} workers={worker ? [worker] : []} defaultWorkerId={worker?.id} onSuccess={load} />
      <ReceiveWorkDialog work={receiveWork} onOpenChange={() => setReceiveWork(null)} onSuccess={load} />
    </div>
  );
}
